<template>
  <div>
    <div v-for="(item, key) in quickBars" :key="key" class="link-item brush-item">
      <div class="link-item-name" @click="goHelpCenter(item)">
        <div class="link-item-image">
          <img :src="item.image" alt="">
        </div>
        <div class="link-item-text">
          <div class="nav">
            <span>{{ item.label }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { handleHelpCenterLink } from '@/utils/tool'
import { info } from '@/api/user'
import { mapGetters } from 'vuex'

const CUSTOMER_SERVICE_APP = Object.freeze({
  LIVE_CHAT: 'LiveChat',
  LIVE_CHAT_AI: 'LiveChatAI',
  SERVICE_CHAT: 'ServiceChat'
})

// ServiceChat widget 載入器
const SERVICE_CHAT_LOADER_SRC = 'https://servicechat.ewin888.com/widget/servicechat-widget-loader.js'

// 取得（或建立）此瀏覽器唯一的訪客 ID。用於組出 {brandCode}-{id}，
// 讓每位匿名訪客有獨立客服房間（ServiceChat 相同 customer account 會共用同一房間）。
function getOrCreateGuestId() {
  const KEY = 'servicechat_guest_id'
  let id = null
  try { id = localStorage.getItem(KEY) } catch (e) { /* localStorage 不可用時忽略 */ }
  if (!id) {
    id = Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
    try { localStorage.setItem(KEY, id) } catch (e) { /* localStorage 不可用時忽略 */ }
  }
  return id
}

export default {
  data() {
    return {
      quickBars: [],
      guestId: getOrCreateGuestId()
    }
  },
  computed: {
    ...mapGetters(['token', 'user', 'memberInfo', 'brand', 'brandCode', 'language']),
    token() {
      return this.$store.getters.token
    },
    // 客服系統設定
    customerServiceInfo() {
      return this.brand.customerServiceInfo || {}
    },
    // app 未設定時，預設 LiveChat
    customerServiceApp() {
      return this.customerServiceInfo.app || CUSTOMER_SERVICE_APP.LIVE_CHAT
    },
    licenseId() {
      return this.customerServiceInfo.secret_key || null
    },
    // ServiceChat 會員識別（brandCode 帶站別給客服）：
    //   已登入 → {brandCode}-{username}
    //   訪客   → {brandCode}-{每個瀏覽器唯一ID}（避免所有訪客共用同一個客服房間）
    serviceChatAccount() {
      const brandCode = this.brandCode
      if (!brandCode) return null
      const username = this.memberInfo && this.memberInfo.username
      if (this.token && username) {
        return `${brandCode}-${username}`
      }
      return `${brandCode}-${this.guestId}`
    },
    // 聊天視窗顯示的客戶名稱
    serviceChatName() {
      if (this.memberInfo) {
        return this.memberInfo.username
      }
      return 'Guest'
    },
    // 只有 app=ServiceChat 且有 service_chat_key 時才回傳設定，否則為 null
    serviceChatConfig() {
      if (this.customerServiceApp !== CUSTOMER_SERVICE_APP.SERVICE_CHAT || !this.customerServiceInfo.service_chat_key) return null
      return {
        loaderSrc: SERVICE_CHAT_LOADER_SRC,
        tenantKey: this.customerServiceInfo.service_chat_key
      }
    },
    // app 或 key 改變時重新初始化客服
    customerServiceSignature() {
      return `${this.customerServiceApp}:${this.customerServiceInfo.secret_key || this.customerServiceInfo.service_chat_key || ''}`
    }
  },
  watch: {
    // 使用 ServiceChat 的站台：會員登入/登出（帳號改變）時，以最新身分重新載入 widget
    serviceChatAccount() {
      if (this.serviceChatConfig) {
        this.initServiceChat()
      }
    },
    // 後端客服設定載入或變更時，依 app 重新初始化
    customerServiceSignature() {
      this.initCustomerService()
    }
  },
  created() {
    this.$nextTick(() => {
      setTimeout(() => {
        this.quickBars = this.$store.state.brand.quickBars.filter((item) => {
          return item.type === 'web'
        })
      }, 1000)
    })
  },
  mounted() {
    this.initCustomerService()
  },
  methods: {
    initCustomerService() {
      switch (this.customerServiceApp) {
        case CUSTOMER_SERVICE_APP.SERVICE_CHAT:
          if (this.serviceChatConfig) this.initServiceChat()
          break
        case CUSTOMER_SERVICE_APP.LIVE_CHAT_AI:
          if (this.customerServiceInfo.secret_key) this.initLiveChatAI()
          break
        case CUSTOMER_SERVICE_APP.LIVE_CHAT:
        default:
          if (this.customerServiceInfo.secret_key) this.initLiveChat()
          break
      }
    },
    initServiceChat() {
      const { loaderSrc: LOADER_SRC, tenantKey: TENANT_KEY } = this.serviceChatConfig

      // 身分是在 script 載入時由 data 屬性決定，登入/登出後需重新載入才能換身分。
      // 移除舊的 loader 與 runtime；runtime 重新執行時會先呼叫 ServiceChatWidget.destroy() 清掉舊實例。
      document.getElementById('servicechat-loader')?.remove()
      document.querySelector('script[data-servicechat-widget-runtime="true"]')?.remove()

      const script = document.createElement('script')
      script.id = 'servicechat-loader'
      script.src = LOADER_SRC
      script.async = true
      script.setAttribute('data-tenant-key', TENANT_KEY)
      script.setAttribute('data-hide-launcher', 'true')

      // 直接帶站內語系（與送給 brand API 的 language 相同），由 ServiceChat 後端自行處理
      if (this.language) script.setAttribute('data-language', this.language)

      // 帶入 customer-account（已登入為 {brandCode}-{username}，訪客為 {brandCode}-{id}）
      // 及顯示名稱；brandCode 尚未載入時 serviceChatAccount 為 null，待 watch 重新初始化。
      if (this.serviceChatAccount) {
        script.setAttribute('data-customer-account', this.serviceChatAccount)
        script.setAttribute('data-customer-name', this.serviceChatName)
      }

      document.body.appendChild(script)
      console.log('[ServiceChat] Loaded', this.serviceChatAccount || 'guest')
    },
    initLiveChat() {
      // licenseId : KT: 16229799
      // licenseId : UB: 19528496
      window.__lc = window.__lc || {}
      window.__lc.license = this.licenseId

      ;(function(n, t, c) {
        function i(n) { return e._h ? e._h.apply(null, n) : e._q.push(n) } var e = { _q: [], _h: null, _v: '2.0', on: function() { i(['on', c.call(arguments)]) }, once: function() { i(['once', c.call(arguments)]) }, off: function() { i(['off', c.call(arguments)]) }, get: function() { if (!e._h) throw new Error("[LiveChatWidget] You can't use getters before load."); return i(['get', c.call(arguments)]) }, call: function() { i(['call', c.call(arguments)]) }, init: function() {
          var n = t.createElement('script'); n.async = !0; n.type = 'text/javascript'; n.src = 'https://cdn.livechatinc.com/tracking.js'; t.head.appendChild(n)
        } }; !n.__lc.asyncInit && e.init(); n.LiveChatWidget = n.LiveChatWidget || e
      }(window, document, [].slice))

      function onVisibilityChanged(data) {
        switch (data.visibility) {
          case 'minimized':
            LiveChatWidget.call('hide')
            break
          case 'hidden':
            break
        }
      }

      // Wait for LiveChatWidget to be ready before setting up events and hiding
      LiveChatWidget.on('ready', () => {
        LiveChatWidget.on('visibility_changed', onVisibilityChanged)
        LiveChatWidget.call('hide')
      })
      console.log('[LiveChat] Loaded')
    },
    initLiveChatAI() {
      const kycStatusMap = {
        0: 'KYC_NONE',
        1: 'KYC_PASS',
        2: 'KYC_PROGRESS'
      }

      if (this.token) {
        info().then(response => {
          const livechatHash = response.data.data.livechat.hash
          const memberInfo = this.user.memberInfo
          const walletInfo = this.user.walletInfo
          // 設定 identity
          window.lc = window.lc || {}
          window.lc.debug = true // 開發用
          window.lc.identity = {
            distinctId: memberInfo.id,
            userHash: livechatHash,
            name: memberInfo.username || 'User',
            attributes: {
              email: memberInfo.email,
              phone: memberInfo.phone,
              vip_last_updated_at: this.$options.filters.dateFormat(memberInfo.vip_last_updated_at),
              kycStatus: kycStatusMap[memberInfo.is_pass_KYC],
              first_deposit_amount: memberInfo.first_deposit_amount,
              second_deposit_amount: memberInfo.second_deposit_amount,
              third_deposit_amount: memberInfo.third_deposit_amount,
              last_withdrawal_updated_at: this.$options.filters.dateFormat(memberInfo.last_withdrawal_updated_at),
              last_withdrawal_amount: memberInfo.last_withdrawal_amount,
              jpy_balance: walletInfo.find(w => w.wallet_type === 'main')?.balance,
              bonus_balance: walletInfo.find(w => w.wallet_type === 'bonus')?.balance,
              usdt_balance: walletInfo.find(w => w.wallet_type === 'USDT')?.balance,
              ltc_balance: walletInfo.find(w => w.wallet_type === 'LTC')?.balance,
              xrp_balance: walletInfo.find(w => w.wallet_type === 'XRP')?.balance,
              btc_balance: walletInfo.find(w => w.wallet_type === 'BTC')?.balance,
              eth_balance: walletInfo.find(w => w.wallet_type === 'ETH')?.balance,
              doge_balance: walletInfo.find(w => w.wallet_type === 'DOGE')?.balance,
              bch_balance: walletInfo.find(w => w.wallet_type === 'BCH')?.balance,
              trc_balance: walletInfo.find(w => w.wallet_type === 'TRC')?.balance,
              bnb_balance: walletInfo.find(w => w.wallet_type === 'BNB')?.balance,
              usdc_balance: walletInfo.find(w => w.wallet_type === 'USDC')?.balance,
              shib_balance: walletInfo.find(w => w.wallet_type === 'SNIB')?.balance,
              birthday: memberInfo.birthday,
              real_name: memberInfo.username,
              vip: this.user.vipInfo.currentVipName
            }
          }
        }).catch(error => {
          console.error(error)
        })
      }
      // 避免重複載入
      if (document.getElementById('livechatAI-script')) return
      const script = document.createElement('script')
      script.src = 'https://app.livechatai.com/embed.js'
      script.setAttribute('data-id', this.customerServiceInfo.secret_key)
      script.id = 'livechatAI-script'
      script.async = true
      script.defer = true

      document.body.appendChild(script)
      console.log('[LiveChatAI] Loaded')
    },
    goHelpCenter(item) {
      const isGuest = !this.token
      const user = this.user
      const userInfo = this.memberInfo
      const vipInfo = user.vipInfo
      const dateFormatter = this.$options.filters.dateFormat
      handleHelpCenterLink(isGuest, item, user, userInfo, vipInfo, dateFormatter)
    }
  }

}
</script>
<style>
/*
 * ServiceChat widget 是渲染在一般 DOM（非 Shadow DOM），本站的全域「裸標籤」CSS
 * 會汙染進 widget。其中 style.css 的 header{position:fixed} 會把 widget 的
 * <header>（.servicechat-widget-head）抽離 grid，導致面板塌陷、訊息區壓成 0、
 * 輸入框浮到最上面。以下重新宣告 widget 各容器該有的定位，隔絕主站樣式。
 */
.servicechat-widget-head {
  position: static !important;
}
.servicechat-widget-panel,
.servicechat-widget-button {
  position: fixed !important;
}
.servicechat-widget-send {
  width: 100% !important;
}

/* 非 theme_gold 站台使用 LiveChatAI，手機版隱藏其浮動元件 */
@media (max-width: 768px) {
  #live-chat-ai-host {
    visibility: hidden !important;
    pointer-events: none !important;
  }
}
</style>
